import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAdminPaymentVerificationDetail, useApprovePaymentProof, useRejectPaymentProof } from '@/hooks/usePaymentProofs'
import { PageHeader } from '@/components/layout'

export const AdminPaymentVerificationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: proof, isLoading, error } = useAdminPaymentVerificationDetail(id || '')
  const approveMutation = useApprovePaymentProof()
  const rejectMutation = useRejectPaymentProof()

  const [rejectReason, setRejectReason] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)

  const handleApprove = async () => {
    if (!confirm('Yakin ingin menyetujui bukti pembayaran ini? Enrollment akan aktif.')) return
    approveMutation.mutate(id || '', {
      onSuccess: () => {
        alert('Bukti pembayaran berhasil disetujui!')
        navigate('/admin/payment-verifications')
      },
    })
  }

  const handleReject = async () => {
    if (!rejectReason.trim() || rejectReason.length < 5) {
      alert('Alasan penolakan harus diisi minimal 5 karakter')
      return
    }
    rejectMutation.mutate(
      { proofId: id || '', reason: rejectReason },
      {
        onSuccess: () => {
          alert('Bukti pembayaran berhasil ditolak')
          navigate('/admin/payment-verifications')
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-white/10 rounded w-48" />
          <div className="h-64 bg-gray-200 dark:bg-white/10 rounded-xl" />
        </div>
      </div>
    )
  }

  if (error || !proof) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 p-4 rounded-xl text-sm border border-red-200 dark:border-red-500/20">
          Gagal memuat data: {error?.message || 'Data tidak ditemukan'}
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Detail Verifikasi"
        breadcrumbs={[
          { label: 'Verifikasi Pembayaran', to: '/admin/payment-verifications' },
          { label: proof.userName },
        ]}
      />

      {/* Proof Info */}
      <div className="bg-white dark:bg-surface-card rounded-xl border border-gray-200 dark:border-white/5 p-6 space-y-4 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">Peserta</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{proof.userName}</p>
            <p className="text-xs text-gray-500 dark:text-text-muted">{proof.userEmail}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">Course</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{proof.courseName}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">Jumlah Dibayarkan</p>
            <p className="text-lg font-bold text-primary">
              Rp {proof.amountPaid.toLocaleString('id-ID')}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">Status</p>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
              proof.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' :
              proof.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' :
              proof.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' :
              proof.status === 'cancelled' ? 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-text-muted' :
              'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-text-muted'
            }`}>
              {proof.status === 'pending' ? 'Menunggu' :
               proof.status === 'approved' ? 'Disetujui' :
               proof.status === 'rejected' ? 'Ditolak' :
               proof.status === 'cancelled' ? 'Dibatalkan' : 'Kedaluwarsa'}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-white/5 pt-4">
          <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-2">Data Transfer</p>
          <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-3 grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-text-muted">Bank Pengirim</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{proof.bankName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-text-muted">No. Rekening</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white font-mono">{proof.accountNumber}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-text-muted">Atas Nama</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{proof.accountHolderName}</p>
            </div>
          </div>
        </div>

        {proof.notes && (
          <div className="border-t border-gray-100 dark:border-white/5 pt-4">
            <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">Catatan Peserta</p>
            <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-white/5 p-3 rounded-lg">{proof.notes}</p>
          </div>
        )}

        {/* Proof Image */}
        <div className="border-t border-gray-100 dark:border-white/5 pt-4">
          <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-2">Bukti Transfer</p>
          <a
            href={proof.proofFileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:text-red-800 dark:hover:text-red-400 font-medium"
          >
            <span className="material-symbols-outlined text-lg">open_in_new</span>
            Lihat Bukti Transfer
          </a>
        </div>

        {proof.rejectionReason && (
          <div className="border-t border-gray-100 dark:border-white/5 pt-4">
            <p className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">Alasan Penolakan</p>
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 p-3 rounded-lg">{proof.rejectionReason}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      {proof.status === 'pending' && (
        <div className="bg-white dark:bg-surface-card rounded-xl border border-gray-200 dark:border-white/5 p-6">
          {!showRejectForm ? (
            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                disabled={approveMutation.isPending || rejectMutation.isPending}
                className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">check_circle</span>
                {approveMutation.isPending ? 'Memproses...' : 'Setujui'}
              </button>
              <button
                onClick={() => setShowRejectForm(true)}
                disabled={approveMutation.isPending || rejectMutation.isPending}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-all shadow-lg shadow-red-600/20 flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">cancel</span>
                Tolak
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider block">
                Alasan Penolakan *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Jelaskan alasan penolakan (minimal 5 karakter)"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowRejectForm(false); setRejectReason('') }}
                  className="flex-1 px-4 py-3 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  Batal
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending || approveMutation.isPending}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-all shadow-lg shadow-red-600/20"
                >
                  {rejectMutation.isPending ? 'Menolak...' : 'Tolak Pembayaran'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
